import { useSelector } from 'react-redux';
import { getSupplierMatchComponent } from '../../Helpers/supplierMatchPredicates';
import {
  SUBCATEGORY_MAX_MATCHES_DEFAULT,
  convertMatchToOption,
} from '../../Helpers/supplierMatches';
import {
  dedupeOptionsById,
  getSubmittedDynamicMatches,
} from '../../Helpers/supplierResolution';

/**
 * Note: This is the order of precedence for options:
 * 1. Supplier match component (excluding what has been deselected)
 * 2. Customer matches
 * 3. Subcategory matches
 *
 * When dynamic Select and Connect is on the list above is bypassed:
 * dynamicMatches is the shown set (empty when every match was filtered out)
 * and the submitted set is the primary plus the completed secondaries.
 *
 * The customer is being matched to a "brand", but as a company we refer to brands
 * as suppliers. There may be one or many suppliers under one brand.
 */
const useSupplierMatch = () => {
  const components = useSelector((store) => store?.components || {});
  const { customerMatches, subcategoryMatches, subcategoryMaxMatches } =
    useSelector((store) => store?.supplierMatches || {});
  const dynamicMatches = useSelector(
    (store) => store?.supplierMatches?.dynamicMatches
  );
  const isDynamicSelectAndConnectEnabled = useSelector(
    (store) =>
      store?.features?.dynamicSelectAndConnect?.enableDynamicSelectAndConnect
  );
  const selectAndConnectBrands = useSelector(
    (store) => store?.submission?.selectAndConnectBrands
  );
  const supplierMatchComponent = getSupplierMatchComponent({ components });
  // The same conversion the saga uses to hand matches to a supplier match
  // component, so every source yields the same option shape.
  const customerMatchOptions = customerMatches?.map(convertMatchToOption);
  const subcategoryMatchOptions = subcategoryMatches?.map(convertMatchToOption);

  const input = supplierMatchComponent?.input || {};
  let options = [];

  if (input?.options && input.options.length > 0) {
    options = input.options;
  } else if (customerMatchOptions && customerMatchOptions.length > 0) {
    options = customerMatchOptions;
  } else if (subcategoryMatchOptions && subcategoryMatchOptions.length > 0) {
    options = subcategoryMatchOptions;
  }

  let selectedOptions = input?.selectedOptions || [];

  if (!supplierMatchComponent) {
    if (customerMatchOptions?.length > 0) {
      selectedOptions = customerMatchOptions;
    }

    if (
      !customerMatchOptions?.length > 0 &&
      subcategoryMatchOptions?.length > 0
    ) {
      selectedOptions = subcategoryMatchOptions;
    }
  }

  let filteredOptions = selectedOptions;

  if (supplierMatchComponent && options?.length > 0) {
    filteredOptions = options.filter((option) =>
      selectedOptions.includes(String(option?.id))
    );
  }

  const maxMatchesToDisplay =
    subcategoryMaxMatches || SUBCATEGORY_MAX_MATCHES_DEFAULT;

  /**
   * We would have already filtered to the options displayed in the supplier match
   * component if it exists, so we only need to filter the options by subcategory
   * max matches now. This is only used when there is no supplier match component
   * on the page.
   */
  if (filteredOptions.length > maxMatchesToDisplay) {
    filteredOptions = filteredOptions.slice(0, maxMatchesToDisplay);
  }

  // Dynamic Select and Connect shows the whole dynamicMatches list (primary
  // first, then secondaries and fallbacks). The primary is submitted with the
  // form and is the sole option handed to a supplier match component; the
  // secondaries the customer ticked are submitted from the Select and Connect
  // page and recorded in the submission ledger, so the submitted set is the
  // primary plus the ledger's completed brands. dynamicMatches is already
  // filtered to exclusive brands and the supplier match page is hidden in
  // dynamic mode, so when it is empty nothing was shown: the other sources
  // must not leak in.
  if (isDynamicSelectAndConnectEnabled) {
    const dynamicOptions = (dynamicMatches || []).map(convertMatchToOption);
    const submittedOptions = getSubmittedDynamicMatches({
      dynamicMatches,
      selectAndConnectBrands,
    }).map(convertMatchToOption);
    const primaryOptions = supplierMatchComponent
      ? filteredOptions
      : submittedOptions.slice(0, 1);

    return {
      matchedAndFilteredBrands: dedupeOptionsById(
        primaryOptions,
        submittedOptions
      ),
      shownBrands: dynamicOptions,
    };
  }

  // Every brand displayed to the customer, in display order, before the
  // selection filter. The thankYouPageRequested event reports these alongside
  // the submitted set so a consumer can tell which shown brands were deselected.
  // A supplier match component displays all of its options, so only the
  // component-less journeys are capped.
  const shownBrands = supplierMatchComponent
    ? options
    : options.slice(0, maxMatchesToDisplay);

  return { matchedAndFilteredBrands: filteredOptions, shownBrands };
};

export default useSupplierMatch;
